import axios from "axios";
import Cookies from "js-cookie";

const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";

// Fonction utilitaire pour convertir un fichier en base64
const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Extraire la partie base64 (enlever le préfixe data:image/jpeg;base64,)
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

// Configuration axios avec intercepteurs pour l'authentification
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Intercepteur pour ajouter le token JWT
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get("token") || localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs de réponse
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/auth/login";
    }
    return Promise.reject(error);
  }
);

/**
 * Service pour la messagerie interne
 */
export const messagerieService = {
  /**
   * Récupère les messages reçus avec pagination et filtres
   */
  getReceivedMessages: async (params = {}) => {
    const {
      page = 1,
      limit = 10,
      search = "",
      statut = "",
      priorite = "",
    } = params;
    const response = await api.get("/messagerie/received", {
      params: { page, limit, search, statut, priorite },
    });
    return response.data;
  },

  /**
   * Récupère les messages envoyés avec pagination et filtres
   */
  getSentMessages: async (params = {}) => {
    const {
      page = 1,
      limit = 10,
      search = "",
      statut = "",
      priorite = "",
    } = params;
    const response = await api.get("/messagerie/sent", {
      params: { page, limit, search, statut, priorite },
    });
    return response.data;
  },

  /**
   * Récupère un message spécifique par ID
   */
  getMessageById: async (messageId) => {
    const response = await api.get(`/messagerie/${messageId}`);
    return response.data;
  },

  /**
   * Crée un nouveau message
   */
  createMessage: async (messageData, file = null) => {
    const requestData = { ...messageData };

    // Si un fichier est fourni, le convertir en base64
    if (file) {
      try {
        const base64Data = await convertFileToBase64(file);
        requestData.file = {
          name: file.name,
          type: file.type,
          data: base64Data,
        };
      } catch (error) {
        console.error("Erreur conversion fichier:", error);
        throw new Error("Erreur lors de la conversion du fichier");
      }
    }

    const response = await api.post("/messagerie", requestData);
    return response.data;
  },

  /**
   * Répondre à un message (RH/Manager/Admin uniquement)
   */
  replyToMessage: async (messageId, reponse) => {
    const response = await api.put(`/messagerie/${messageId}/reply`, {
      reponse,
    });
    return response.data;
  },

  /**
   * Mettre à jour le statut d'un message
   */
  updateMessageStatus: async (messageId, statut) => {
    const response = await api.put(`/messagerie/${messageId}/status`, {
      statut,
    });
    return response.data;
  },

  /**
   * Supprimer un message
   */
  deleteMessage: async (messageId) => {
    const response = await api.delete(`/messagerie/${messageId}`);
    return response.data;
  },

  /**
   * Récupère la liste des employés pour le dropdown
   */
  getEmployees: async () => {
    const response = await api.get("/messagerie/employees");
    return response.data;
  },

  /**
   * Récupère les statistiques de messagerie
   */
  getMessageStats: async () => {
    const response = await api.get("/messagerie/stats");
    return response.data;
  },
};

export default messagerieService;
